import {
  type IFirestoreModel,
  type Timestamp,
} from '@principle-theorem/shared';

/**
 * The kinds of Source a Website Snapshot can be read from. Only the practice
 * website ships today; the kind is stored so other Sources can be added later
 * without reshaping existing snapshots.
 */
export enum WebsiteSourceKind {
  Website = 'website',
}

/**
 * Where a Website Snapshot was read from. `url` is the normalised address that
 * was requested, which can differ from the resolved root page URL when the
 * site redirects.
 */
export interface IWebsiteSource {
  kind: WebsiteSourceKind;
  url: string;
}

/**
 * One page of readable text captured from a Source. `url` is the resolved page
 * address, and is the only Provenance an Import Proposal value may cite.
 */
export interface IWebsiteSnapshotPage {
  url: string;
  title?: string;
  text: string;
  isRoot: boolean;
}

/**
 * The latest read of a Source, stored as one document per Source under the
 * practice so a repeat import does not need to crawl the site again.
 */
export interface IWebsiteSnapshot extends IFirestoreModel {
  source: IWebsiteSource;
  fetchedAt: Timestamp;
  pagesRequested: number;
  failedPages: number;
  pages: IWebsiteSnapshotPage[];
}

export const WEBSITE_SNAPSHOT_MAX_PAGES = 25;
export const WEBSITE_SNAPSHOT_MAX_PAGE_CHARS = 20_000;
/**
 * Encoded size budget for a snapshot document, kept well under Firestore's
 * 1 MiB document limit.
 */
export const WEBSITE_SNAPSHOT_MAX_BYTES = 900_000;
/**
 * Snapshots at least this old are no longer offered for reuse; the import
 * reads the website again instead.
 */
export const WEBSITE_SNAPSHOT_STALE_AFTER_DAYS = 7;

/**
 * Paths dental practice websites commonly give the pages a Knowledge Base
 * draws on. Every fresh read tries them against the home page's resolved
 * address, because the home page's own links cannot be relied on: Exa returns
 * at most 10 of them and skips the navigation menu, and on a real practice
 * site they missed the About, FAQ and Services pages that these paths found.
 * Exa does not charge for a path the site does not have. See ADR 0006.
 */
export const WEBSITE_IMPORT_COMMON_PATHS = [
  '/about',
  '/about-us',
  '/our-team',
  '/team',
  '/meet-the-team',
  '/faq',
  '/faqs',
  '/services',
  '/our-services',
  '/treatments',
  '/contact',
  '/contact-us',
  '/fees',
  '/pricing',
  '/financial-info',
  '/payment-options',
  '/payments',
  '/new-patients',
  '/first-visit',
  '/patient-information',
  '/emergency',
  '/emergency-dentist',
  '/health-funds',
  '/location',
];

/**
 * The Exa search run on every fresh read, restricted to the practice's own
 * domain, to find pages that neither the home page's links nor
 * `WEBSITE_IMPORT_COMMON_PATHS` reach. Its words are the topics a Knowledge
 * Base draws on, so pages about them rank first. No other website is ever
 * searched. See ADR 0006.
 */
export const WEBSITE_IMPORT_SEARCH_QUERY =
  'dental practice services fees faq about team contact';

/**
 * How many addresses the domain search asks Exa for. On a real practice site,
 * 25 found every page the common paths found plus deeper FAQ and financial
 * information pages, for about US$0.02 a search; Exa charges extra for each
 * result above 10.
 */
export const WEBSITE_IMPORT_SEARCH_RESULTS = 25;
